/**
 * Fase 2A · P0-1 · EmpleadoClientes
 * Lista read-only de clientes del centro. Sin datos financieros.
 * Backend: GET /api/customers · rol empleado permitido (customers.py:119).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Skeleton } from "../../components/ui/skeleton";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  MagnifyingGlass, User as UserIcon, Phone, EnvelopeSimple, CarSimple,
  AddressBook,
} from "@phosphor-icons/react";

export default function EmpleadoClientes() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customers?limit=300");
        const items = Array.isArray(data) ? data : (data?.items || []);
        setCustomers(items);
      } catch (e) {
        toast.error("No se pudieron cargar los clientes");
        setCustomers([]);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!customers) return [];
    const term = q.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const plates = (c.license_plates || []).join(" ").toLowerCase();
      return name.includes(term) || phone.includes(term) || email.includes(term) || plates.includes(term);
    });
  }, [customers, q]);

  if (customers === null) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 rounded-sm" />
        <Skeleton className="h-24 rounded-sm" />
        <Skeleton className="h-24 rounded-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="empleado-clientes">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
          CRM operativo
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
          Clientes del centro
        </h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          Consulta contacto, matrículas y datos operativos. Sin acceso a facturación.
        </p>
      </header>

      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, teléfono, email o matrícula…"
          className="pl-9"
          data-testid="empleado-clientes-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="ag-card p-8 text-center" data-testid="empleado-clientes-empty">
          <AddressBook size={32} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
          <div className="text-sm text-[var(--ag-text-dim)] mt-3">
            {q ? "Sin resultados para tu búsqueda." : "Aún no hay clientes en el centro."}
          </div>
        </div>
      ) : (
        <div className="ag-card p-0 divide-y divide-[var(--ag-border)]">
          <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] bg-[var(--ag-midnight)]/40">
            {filtered.length} {filtered.length === 1 ? "cliente" : "clientes"}
          </div>
          {filtered.slice(0, 100).map((c) => (
            <CustomerRow key={c.id} customer={c} />
          ))}
          {filtered.length > 100 && (
            <div className="px-4 py-3 text-[11px] text-[var(--ag-text-dim)] text-center">
              Se muestran los primeros 100 · afina tu búsqueda para ver más
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CustomerRow({ customer }) {
  const plates = (customer.license_plates || []).slice(0, 3);
  return (
    <div className="p-4 hover:bg-[var(--ag-midnight)]/40 transition-colors" data-testid={`empleado-cust-${customer.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <UserIcon size={14} weight="duotone" className="text-[var(--ag-gold)] shrink-0" />
            <span className="font-display font-bold text-sm truncate">{customer.name || "(Sin nombre)"}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[var(--ag-text-dim)] mt-1 flex-wrap">
            {customer.phone && (
              <a href={`tel:${customer.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--ag-gold)]">
                <Phone size={11} weight="bold" />
                <span className="tabular-nums">{customer.phone}</span>
              </a>
            )}
            {customer.email && (
              <a href={`mailto:${customer.email}`} className="inline-flex items-center gap-1 hover:text-[var(--ag-gold)] truncate">
                <EnvelopeSimple size={11} weight="bold" />
                <span className="truncate">{customer.email}</span>
              </a>
            )}
          </div>
        </div>
        {plates.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap shrink-0">
            {plates.map((p) => (
              <span key={p} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded font-mono-tab text-[10px] font-bold">
                <CarSimple size={10} weight="duotone" className="text-[var(--ag-gold)]" />
                {p}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
